import { ArrowLeft, Compass, Lock } from 'lucide-react';
import type { ComponentType } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';

export function ForbiddenPage() {
  return (
    <ErrorLayout
      code="403"
      icon={Lock}
      title="You do not have access to this page"
      description="Either your roles do not include the required permission, or your organisation has switched this module off. If you believe this is wrong, ask an administrator."
    />
  );
}

export function NotFoundPage() {
  return (
    <ErrorLayout
      code="404"
      icon={Compass}
      title="Page not found"
      description="The page you asked for does not exist, or has moved."
    />
  );
}

function ErrorLayout({
  code,
  title,
  description,
  icon: Icon,
}: {
  code: string;
  title: string;
  description: string;
  icon: ComponentType<{ className?: string }>;
}) {
  const navigate = useNavigate();

  return (
    <div className="relative flex min-h-[65vh] flex-col items-center justify-center overflow-hidden px-6 text-center">
      <div className="bg-aurora absolute inset-0 -z-10 opacity-60" aria-hidden="true" />

      {/* The code is set large and low-contrast: it identifies the failure for anyone
          reporting it, without competing with the sentence that explains it. */}
      <p
        className="pointer-events-none select-none font-mono text-8xl font-bold tracking-tighter text-foreground/[0.06] sm:text-9xl"
        aria-hidden="true"
      >
        {code}
      </p>

      <div className="-mt-10 flex flex-col items-center gap-5 sm:-mt-14">
        <span className="flex size-14 items-center justify-center rounded-2xl border border-border bg-card shadow-md">
          <Icon className="size-6 text-muted-foreground" aria-hidden="true" />
        </span>

        <div className="max-w-md space-y-2">
          <h1 className="text-2xl font-semibold tracking-tight">
            {/* The numeral is decorative above, so the code is restated here where a
                screen reader will reach it as part of the heading. */}
            <span className="sr-only">Error {code}: </span>
            {title}
          </h1>
          <p className="text-sm leading-relaxed text-muted-foreground">{description}</p>
        </div>

        {/* Full-width and stacked on a phone, where a 44px control at its natural
            width leaves an error page's two actions marooned mid-screen. */}
        <div className="flex w-full max-w-xs flex-col-reverse items-stretch gap-2 sm:w-auto sm:max-w-none sm:flex-row sm:flex-wrap sm:items-center sm:justify-center">
          <Button variant="secondary" iconMotion="back" onClick={() => navigate(-1)}>
            <ArrowLeft aria-hidden="true" />
            Go back
          </Button>
          <Button asChild>
            <Link to="/">Back to dashboard</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
